import React from 'react';

interface CardData {
  icon: React.ReactNode;
  title: string;
  text: string;
  delay: string;
}

const cards: CardData[] = [
  {
    icon: (
      <svg className="w-7 h-7 draw-stroke" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.5 3 2.5 15 0 18M12 3c-2.5 3-2.5 15 0 18M3 12h18" />
      </svg>
    ),
    title: 'Psicologia Analitica',
    text: 'Esploriamo insieme simboli, sogni e archetipi: ciò che la mente razionale non vede, ma che guida ogni giorno le tue scelte. Un dialogo profondo nella tradizione junghiana.',
    delay: '0ms',
  },
  {
    icon: (
      <svg className="w-7 h-7 draw-stroke" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7c3-4 6-4 9 0s6 4 9 0M3 12c3-4 6-4 9 0s6 4 9 0M3 17c3-4 6-4 9 0s6 4 9 0" />
      </svg>
    ),
    title: 'Ipnosi Ericksoniana',
    text: "Accediamo a uno stato di coscienza naturale dove l'attenzione si rivolge all'interno. Nessuna imposizione: la persona viene accompagnata, con delicatezza, verso le proprie risorse.",
    delay: '120ms',
  },
  {
    icon: (
      <svg className="w-7 h-7 draw-stroke" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
        <circle cx="9" cy="12" r="6" />
        <circle cx="15" cy="12" r="6" />
      </svg>
    ),
    title: 'Integrazione',
    text: "La prospettiva junghiana e l'ipnosi ericksoniana convivono in uno spazio personalizzato: ascolto profondo, rispetto dei tempi interiori e attenzione a ciò che chiede di essere accolto.",
    delay: '240ms',
  },
];

function Words({ text, base = 0, step = 80 }: { text: string; base?: number; step?: number }) {
  return (
    <>
      {text.split(' ').map((w, i) => (
        <span key={i} className="word-reveal">
          <span style={{ ['--wd' as any]: `${base + i * step}ms` }}>{w}&nbsp;</span>
        </span>
      ))}
    </>
  );
}

export default function Approccio() {
  return (
    <section id="approccio" className="relative py-28 bg-cream border-y border-forest/5 overflow-hidden">
      <div
        className="absolute -right-16 sm:-right-32 top-10 w-[320px] h-[320px] md:w-[420px] md:h-[420px] rounded-full bg-sage/10 blur-3xl pointer-events-none"
        data-parallax="0.06"
        style={{ transform: 'translateY(var(--py, 0))' }}
        aria-hidden
      />
      <div
        className="absolute -left-20 sm:-left-40 bottom-0 w-[300px] h-[300px] md:w-[440px] md:h-[440px] rounded-full bg-clay/10 blur-3xl pointer-events-none"
        data-parallax="0.04"
        style={{ transform: 'translateY(var(--py, 0))' }}
        aria-hidden
      />

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          <div className="lg:col-span-4 reveal">
            <div className="flex items-baseline gap-4">
              <span className="font-serif text-5xl italic text-sage/70 leading-none">01</span>
              <span className="text-xs uppercase tracking-[0.3em] text-clay font-semibold">L'approccio</span>
            </div>
          </div>
          <div className="lg:col-span-8 space-y-6">
            <h3 className="reveal font-serif text-4xl md:text-5xl text-forestDark leading-[1.15] tracking-tight">
              <Words text="Due strumenti profondi" />
              <em className="italic font-light text-sage">
                <Words text="per un solo percorso:" base={280} />
              </em>
              <Words text="il tuo." base={720} />
            </h3>
            <p className="reveal text-charcoal/75 leading-relaxed max-w-2xl" style={{ ['--d' as any]: '160ms' }}>
              Mi occupo di ansia, attacchi di panico e difficoltà emotive, di momenti di depressione e crisi personali o relazionali. Lavoro con l'elaborazione di traumi, il rafforzamento dell'autostima, i legami affettivi complessi e i vissuti legati alla sfera intima — con delicatezza e rispetto.
            </p>
          </div>
        </div>

        <div className="relative">
          <div className="reveal approccio-thread hidden md:block" style={{ ['--d' as any]: '200ms' }} aria-hidden />
          <div className="reveal approccio-node hidden md:block" style={{ ['--d' as any]: '200ms' }} aria-hidden />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 relative z-10">
            {cards.map((c, i) => (
              <div
                key={c.title}
                className="reveal tilt-card group relative p-8 bg-beigeBG rounded-2xl border border-forest/5 shadow-sm space-y-6 smooth-transition hover:shadow-xl hover:-translate-y-1.5"
                style={{ ['--d' as any]: c.delay }}
              >
                <div className="icon-pop w-14 h-14 rounded-xl bg-sage/10 flex items-center justify-center text-forest group-hover:bg-sage/20 smooth-transition">
                  {c.icon}
                </div>
                <h4 className="font-serif text-2xl text-forestDark font-medium">{c.title}</h4>
                <p className="text-sm text-charcoal/80 leading-relaxed">{c.text}</p>
                <div className="pt-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-sage/70 font-semibold">
                  <span className="inline-block w-6 h-px bg-sage/40" />
                  {i === 0 && <span>Jung · Inconscio · Simboli</span>}
                  {i === 1 && <span>Erickson · Trance · Risorse</span>}
                  {i === 2 && <span>Ascolto · Tempo · Rispetto</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
